import {ApiBaseService, ApiV2Service} from '@/services/api.base.service';
import store from '@/store/store';
import Transformation from '@/classes/transformation';
import {Audit, Device, Gateway, GatewayType, SUB_ALLOCATE_RESULT, SuccessResponse} from '@/types';

export interface SearchGatewayResponse {
  gateways: Gateway[];
  columnFilters: any;
  total: number;
}
export class GatewayService {
  private api: ApiBaseService;
  private apiV2: ApiBaseService;
  constructor() {
    this.api = ApiV2Service;
    this.apiV2 = ApiV2Service;
  }

  public async getGateways(
    take: number = 0, skip: number = 0, sort: string = '',
    desc: boolean = false,
    filters: Record<string, Record<string, boolean>> = {},
    projectId: number | null = null,
    searchNames: string | null = null,
  ): Promise<SearchGatewayResponse> {
    const organisation = store.getters.activeOrganisation[0].id;
    let filtersString = '';

    const filterQuery = Transformation.filterMapToQueryString(filters);

    if (filterQuery) {
      filtersString += `&${filterQuery}`;
    }

    if (projectId) {
      filtersString += `&projectId=${projectId}`;
    }
    if (searchNames) {
      filtersString += `&searchNames=${encodeURIComponent(searchNames)}`;
    }

    return this.apiV2.get(
      `organisation/${organisation}/gateways/search?` +
      `take=${take}&skip=${skip}&sort=${sort}&desc=${desc}&includeColumnFilters=true${filtersString}`);

  }

  public async getInventoryGateways(
    take: number = 0, skip: number = 0, sort: string = '',
    desc: boolean = false,
    filters: Record<string, Record<string, boolean>> = {},
    searchNames: string | null = null,
  ): Promise<SearchGatewayResponse> {
    const organisation = store.getters.activeOrganisation[0].id;
    let filtersString = '';

    const filterQuery = Transformation.filterMapToQueryString(filters);

    if (filterQuery) {
      filtersString += `&${filterQuery}`;
    }
    if (searchNames) {
      filtersString += `&searchNames=${encodeURIComponent(searchNames)}`;
    }

    return this.apiV2.get(
      `organisation/${organisation}/inventory/gateways/search?` +
      `take=${take}&skip=${skip}&sort=${sort}&desc=${desc}&includeColumnFilters=true${filtersString}`);

  }

  public async getInventoryGateway(gatewayId: number): Promise<Gateway> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.apiV2.get(`organisation/${organisation}/inventory/gateways/${gatewayId}`);
  }

  public async getGateway(gatewayId: number): Promise<Gateway> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.apiV2.get(`organisation/${organisation}/gateways/${gatewayId}`);
  }

  public async getGatewayStatus(gatewayId: number): Promise<Gateway> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.apiV2.get(`organisation/${organisation}/gateways/${gatewayId}/status`);
  }

  public async assignGateway(gatewayId: number, projectId: number): Promise<Gateway> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.apiV2.post(`organisation/${organisation}/gateways/${gatewayId}/assign`, { project_id: projectId });
  }

  public async assignGateways(gatewayIds: number[], projectId: number): Promise<Gateway> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.apiV2.post(`organisation/${organisation}/gateways/assign/bulk`, { project_id: projectId, gateway_ids: gatewayIds });
  }

  public async unassignGateway(gatewayId: number): Promise<Gateway> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.apiV2.post(`organisation/${organisation}/gateways/${gatewayId}/unassign`, { unmount: true });
  }

  public async mountGateway(gatewayId: number, details: { locationId: number | null, siteId?: number, positionId?: number }): Promise<Gateway> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.apiV2.post(`organisation/${organisation}/gateways/${gatewayId}/mount`, { location_id: details.locationId });
  }

  public async unmountGateway(gatewayId: number, details: { locationId?: number, siteId?: number, positionId?: number }): Promise<Gateway> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.apiV2.post(`organisation/${organisation}/gateways/${gatewayId}/unmount`, details);
  }

  public async addGateway(gateway: {
    name: string; eui: string;
    keep_alive?: number | null;
    require_authenticated_selection: boolean;
    gateway_type_id: number;
    project_id?: number;
    iccid: string;
    other_connection_type: boolean
  }): Promise<Gateway> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.apiV2.post(`organisation/${organisation}/gateways`, gateway);
  }

  public async updateGateway(gateway: { id: number; keep_alive?: number | null, basic_station?: boolean, alerts_enabled?: boolean, name?: string }): Promise<Gateway> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.apiV2.put(`organisation/${organisation}/gateways/${gateway.id}`, gateway);
  }

  public async removeGateway(gatewayId: number): Promise<{ success: string }> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.apiV2.delete(`organisation/${organisation}/gateways/${gatewayId}`);
  }

  public async getGatewayTypes(): Promise<GatewayType[]> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.apiV2.get(`organisation/${organisation}/gatewayTypes/`);
  }

  public async checkUnique(eui: string): Promise<{ valid: boolean }> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.apiV2.get(`organisation/${organisation}/gateways/check-unique?eui=${eui}`);
  }

  public async checkGlobalUnique(eui: string): Promise<{ valid: boolean }> {
    return this.apiV2.get(`gateways/check-global-unique?eui=${eui}`);
  }

  public async getGatewayAudit(gatewayId: number): Promise<Audit[]> {
    const organisation = store.getters.activeOrganisation[0].id;
    return this.api.get(`audit/organisation/${organisation}/gateway/${gatewayId}`);
  }

  public async export(projectId: number | null, queryData: any, searchName: string): Promise<{ job_id: string }> {
    const organisation = store.getters.activeOrganisationId;

    const filters = Transformation.queryDataToExportPayload(queryData.filters);

    const eventPayload = {
      ...(projectId ? { project_id: projectId } : null),
      ...(filters ? { filters } : null),
      sort_params: {
        ...(queryData.sort ? {
          order: {
            [queryData.sort]: queryData.desc ? 'DESC' : 'ASC',
          },
        } : null),
      },
      ...(searchName ? { search_name: searchName } : null),
    };
    return this.api.post(`organisation/${organisation}/gateways/export`, eventPayload);
  }

  public async pairSim(gatewayId: number, options: { simId?: number, otherConnectionType?: boolean }): Promise<Gateway> {
    const organisation = store.getters.activeOrganisation[0].id;
    const payload: { sim_card_id?: number, other_connection_type?: boolean } = { };
    if (options.otherConnectionType) {
      payload.other_connection_type = true;
    } else {
      payload.sim_card_id = options.simId;
    }
    return await this.api.post(`organisation/${organisation}/gateways/${gatewayId}/pair`, payload, { ignoreErrors: true });
  }

  public async pairSims(pairs: Array<{gatewayId: number, simId?: number, otherConnectionType?: boolean}>): Promise<Gateway[]> {
    const organisation = store.getters.activeOrganisation[0].id;
    const pairsTransformed = pairs.map((p) => {
      const payload: { gateway_id: number, sim_card_id?: number, other_connection_type?: boolean } = {
        gateway_id: p.gatewayId,
      };
      if (p.otherConnectionType) {
        payload.other_connection_type = true;
      } else {
        payload.sim_card_id = p.simId;
      }
      return payload;
    });
    return this.api.post(`organisation/${organisation}/gateways/pair`, pairsTransformed);
  }

  public async unpairSims(gatewayIds: number[]): Promise<Gateway[]> {
    const organisation = store.getters.activeOrganisation[0].id;
    const payload = {
      gateway_ids: gatewayIds,
    };
    return this.api.post(`organisation/${organisation}/gateways/unpair`, payload);
  }

  public async subscribe(gatewayId: number, useActivatedSubscriptions: boolean = false): Promise<{success: string | null, result: SUB_ALLOCATE_RESULT}> {
    const organisation = store.getters.activeOrganisation[0].id;
    const resp: {success: string | null, result: SUB_ALLOCATE_RESULT} = await this.api.put(`organisation/${organisation}/gateways/${gatewayId}/activate`, { useActivatedSubscriptions });

    return resp;
  }

  public async canSubscribe(body: { gatewayTypeId: number, simCardId: number, useActivatedSubscriptions: boolean, otherConnectionType: boolean }): Promise<{gateway: Gateway | null, result: SUB_ALLOCATE_RESULT}> {
    const organisation = store.getters.activeOrganisation[0].id;
    const result: { subscription_error_code: number } = await this.api.post(`organisation/${organisation}/gateways/canSubscribe`, body,
      {
        noToastFilters: [ {status: 422, gives: (resp: any) => ({ data: { subscription_error_code: resp.data.subscription_error_code }}) } ],
      });

    return this.processSubscriptionResult(result);
  }
  private processSubscriptionResult(response: Gateway | { subscription_error_code: number }): {gateway: Gateway | null, result: SUB_ALLOCATE_RESULT} {
    const result: {gateway: Gateway | null, result: SUB_ALLOCATE_RESULT} = {
      gateway: null,
      result: 'ok',
    };
    if ('subscription_error_code' in response) {
      switch (response.subscription_error_code) {
        case 0:
        case -2:
          result.result = 'no_sub';
          break;
        case -1:
          result.result = 'no_sim';
          break;
        case -3:
          result.result = 'not_expired';
          break;
        case -4:
          result.result = 'is_sigfox';
          break;
        case -5:
          result.result = 'no_expiration';
          break;
        case -6:
          result.result = 'no_allocations';
          break;
        case -7:
          result.result = 'allocation_limit_reached';
          break;
        case -8:
          result.result = 'resub_not_supported';
          break;
        default:
          throw new Error('Invalid subscription_error_code');
      }
    } else if ('id' in response) {
      result.gateway = response;
    }

    return result;
  }
}

export default new GatewayService();
